import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Calendar, Plus, Trash2, Info } from "lucide-react";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { 
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

// Sample products for testing
const sampleProducts = [
  { id: 1, name: "Organic Green Apple", category: "Fruits", price: 3.99, cost: 2.50, discountPrice: 3.50, discountPercent: 12, margin: 28, selected: false, limitPerCustomer: 0 },
  { id: 2, name: "Premium Mineral Water", category: "Beverages", price: 0.99, cost: 0.40, discountPrice: 0.89, discountPercent: 10, margin: 55, selected: false, limitPerCustomer: 0 },
  { id: 3, name: "Chocolate Cookies", category: "Snacks", price: 2.49, cost: 1.20, discountPrice: 1.99, discountPercent: 20, margin: 40, selected: false, limitPerCustomer: 5 },
  { id: 4, name: "Fresh Bread Loaf", category: "Bakery", price: 1.99, cost: 0.90, discountPrice: 1.79, discountPercent: 10, margin: 50, selected: false, limitPerCustomer: 2 },
  { id: 5, name: "Canned Tuna", category: "Canned Goods", price: 4.99, cost: 3.00, discountPrice: 3.99, discountPercent: 20, margin: 25, selected: false, limitPerCustomer: 0 },
  { id: 6, name: "Milk 1L", category: "Dairy", price: 3.49, cost: 2.20, discountPrice: 2.99, discountPercent: 14, margin: 26, selected: false, limitPerCustomer: 3 },
];

const CreatePromotionTab: React.FC = () => {
  const [selectedPromotionType, setSelectedPromotionType] = useState("% Discount");
  const [selectedChannel, setSelectedChannel] = useState("All");
  const [selectedTargetSegment, setSelectedTargetSegment] = useState("All");
  const [autoApply, setAutoApply] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [products, setProducts] = useState(sampleProducts);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [bundlePrice, setBundlePrice] = useState("");
  
  // Customer targeting state
  const [targetCustomerGroups, setTargetCustomerGroups] = useState<string[]>([]);
  const [targetCooperatives, setTargetCooperatives] = useState<string[]>([]);
  
  // Buy X Get Y specific state
  const [buyXProducts, setBuyXProducts] = useState<any[]>([]);
  const [getFreeYProducts, setGetFreeYProducts] = useState<any[]>([]);
  const [buyQty, setBuyQty] = useState(1);
  const [getQty, setGetQty] = useState(1);

  const filteredProducts = products.filter(product => 
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDiscountPriceChange = (id: number, value: string) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;
    
    setProducts(products.map(product => {
      if (product.id === id) {
        const originalPrice = product.price;
        const discountPercent = ((originalPrice - numValue) / originalPrice * 100).toFixed(1);
        const margin = ((numValue - product.cost) / numValue * 100).toFixed(1);
        
        return {
          ...product,
          discountPrice: numValue,
          discountPercent: parseFloat(discountPercent),
          margin: parseFloat(margin)
        };
      }
      return product;
    }));
  };

  const handleLimitChange = (id: number, value: string) => {
    const numValue = value === "" ? 0 : parseInt(value);
    if (value !== "" && isNaN(numValue)) return;
    
    setProducts(products.map(product => 
      product.id === id ? { ...product, limitPerCustomer: numValue } : product
    ));
  };

  const toggleSelectAllProducts = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setProducts(products.map(product => ({ ...product, selected: newSelectAll })));
  };

  const toggleProductSelection = (id: number) => {
    setProducts(products.map(product => 
      product.id === id ? { ...product, selected: !product.selected } : product
    ));
    
    const allSelected = products.filter(p => p.id !== id).every(p => p.selected);
    const targetSelected = !products.find(p => p.id === id)?.selected;
    setSelectAll(allSelected && targetSelected);
  };

  const addProductToBuyX = (product: any) => {
    if (!buyXProducts.find(p => p.id === product.id)) {
      setBuyXProducts([...buyXProducts, product]);
    }
  };

  const addProductToGetY = (product: any) => {
    if (!getFreeYProducts.find(p => p.id === product.id)) {
      setGetFreeYProducts([...getFreeYProducts, product]);
    }
  };

  const removeProductFromBuyX = (productId: number) => {
    setBuyXProducts(buyXProducts.filter(p => p.id !== productId));
  };

  const removeProductFromGetY = (productId: number) => {
    setGetFreeYProducts(getFreeYProducts.filter(p => p.id !== productId));
  };

  const toggleCustomerGroup = (group: string) => {
    setTargetCustomerGroups(prev => 
      prev.includes(group)
        ? prev.filter(g => g !== group)
        : [...prev, group]
    );
  };

  const toggleCooperative = (coop: string) => {
    setTargetCooperatives(prev => 
      prev.includes(coop)
        ? prev.filter(c => c !== coop)
        : [...prev, coop]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Create New Promotion</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Basic Information</h3>
              
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="promotion-title">Promotion Title</Label>
                  <Input id="promotion-title" placeholder="Enter promotion title" />
                </div>
                
                <div className="grid gap-2">
                  <Label>Promotion Type</Label>
                  <Select value={selectedPromotionType} onValueChange={setSelectedPromotionType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select promotion type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="% Discount">Individual Product Discounts</SelectItem>
                      <SelectItem value="Buy X Get Y">Buy X Get Y Free</SelectItem>
                      <SelectItem value="Bundle Deal">Bundle Deal</SelectItem>
                      <SelectItem value="Voucher Code">Voucher Code</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            {/* Distribution */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Distribution</h3>
              
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label>Channel</Label>
                  <ToggleGroup type="single" value={selectedChannel} onValueChange={(value) => value && setSelectedChannel(value)}>
                    <ToggleGroupItem value="POS" className="flex-1">POS</ToggleGroupItem>
                    <ToggleGroupItem value="JimatShop" className="flex-1">JimatShop</ToggleGroupItem>
                    <ToggleGroupItem value="All" className="flex-1">All Channels</ToggleGroupItem>
                  </ToggleGroup>
                </div>
              </div>
            </div>
            
            {/* Customer Targeting - New Section */}
            <div className="space-y-4 border p-4 rounded-md bg-slate-50">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium">Customer Targeting</h3>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <Info className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">Target specific customer segments or cooperative members for this promotion</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="customer-segments">
                  <AccordionTrigger>Customer Segments</AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="all-customers" 
                          checked={targetCustomerGroups.includes('all')}
                          onCheckedChange={() => toggleCustomerGroup('all')}
                        />
                        <label htmlFor="all-customers">All Customers</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="regular-customers" 
                          checked={targetCustomerGroups.includes('regular')}
                          onCheckedChange={() => toggleCustomerGroup('regular')}
                        />
                        <label htmlFor="regular-customers">Regular Customers</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="premium-customers" 
                          checked={targetCustomerGroups.includes('premium')}
                          onCheckedChange={() => toggleCustomerGroup('premium')}
                        />
                        <label htmlFor="premium-customers">Premium Customers</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="first-time-customers" 
                          checked={targetCustomerGroups.includes('first-time')}
                          onCheckedChange={() => toggleCustomerGroup('first-time')}
                        />
                        <label htmlFor="first-time-customers">First-time Customers</label>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="coop-members">
                  <AccordionTrigger>Cooperative Members</AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="all-coops" 
                          checked={targetCooperatives.includes('all')}
                          onCheckedChange={() => toggleCooperative('all')}
                        />
                        <label htmlFor="all-coops">All Cooperatives</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="retail-coop-a" 
                          checked={targetCooperatives.includes('Retail Cooperative A')}
                          onCheckedChange={() => toggleCooperative('Retail Cooperative A')}
                        />
                        <label htmlFor="retail-coop-a">Retail Cooperative A</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="school-coop-b" 
                          checked={targetCooperatives.includes('School Cooperative B')}
                          onCheckedChange={() => toggleCooperative('School Cooperative B')}
                        />
                        <label htmlFor="school-coop-b">School Cooperative B</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="agricultural-coop-c" 
                          checked={targetCooperatives.includes('Agricultural Cooperative C')}
                          onCheckedChange={() => toggleCooperative('Agricultural Cooperative C')}
                        />
                        <label htmlFor="agricultural-coop-c">Agricultural Cooperative C</label>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <Label>Member Tiers</Label>
                      <div className="grid grid-cols-3 gap-2 mt-2">
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="regular-members" 
                            checked={targetCustomerGroups.includes('regular-members')}
                            onCheckedChange={() => toggleCustomerGroup('regular-members')}
                          />
                          <label htmlFor="regular-members">Regular</label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="silver-members" 
                            checked={targetCustomerGroups.includes('silver-members')}
                            onCheckedChange={() => toggleCustomerGroup('silver-members')}
                          />
                          <label htmlFor="silver-members">Silver</label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="gold-members" 
                            checked={targetCustomerGroups.includes('gold-members')}
                            onCheckedChange={() => toggleCustomerGroup('gold-members')}
                          />
                          <label htmlFor="gold-members">Gold</label>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
              
              <div className="flex flex-wrap gap-2 mt-2">
                {targetCustomerGroups.length > 0 && targetCustomerGroups[0] !== 'all' && (
                  <>
                    <span className="text-sm font-medium mr-2">Selected Customer Groups:</span>
                    {targetCustomerGroups.map(group => (
                      <Badge key={group} variant="outline" className="bg-green-50">
                        {group.replace('-', ' ')}
                        <button 
                          className="ml-1 text-xs" 
                          onClick={() => toggleCustomerGroup(group)}
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {targetCooperatives.length > 0 && targetCooperatives[0] !== 'all' && (
                  <>
                    <span className="text-sm font-medium mr-2">Selected Cooperatives:</span>
                    {targetCooperatives.map(coop => (
                      <Badge key={coop} variant="outline" className="bg-blue-50">
                        {coop}
                        <button 
                          className="ml-1 text-xs" 
                          onClick={() => toggleCooperative(coop)}
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </>
                )}
              </div>
            </div>
            
            {/* Period */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Promotion Period</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="start-date">Start Date</Label>
                  <div className="relative">
                    <Input 
                      id="start-date" 
                      type="date" 
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                    <Calendar className="absolute right-2 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                  </div>
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="end-date">End Date</Label>
                  <div className="relative">
                    <Input 
                      id="end-date" 
                      type="date" 
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                    <Calendar className="absolute right-2 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
                  </div>
                </div>
              </div>
            </div>
            
            {/* Products Section based on Promotion Type */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Products</h3>
                
                <div className="flex items-center space-x-2">
                  <Input 
                    placeholder="Search products..." 
                    className="w-48"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              
              {selectedPromotionType === "% Discount" && (
                <div className="border rounded-md overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox 
                            checked={selectAll}
                            onCheckedChange={toggleSelectAllProducts}
                            id="select-all"
                          />
                        </TableHead>
                        <TableHead>Product Name</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Normal Price (RM)</TableHead>
                        <TableHead>Discount Price (RM)</TableHead>
                        <TableHead>Discount %</TableHead>
                        <TableHead>Margin %</TableHead>
                        <TableHead>Limit Per Customer</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredProducts.length > 0 ? (
                        filteredProducts.map((product) => (
                          <TableRow key={product.id}>
                            <TableCell>
                              <Checkbox 
                                checked={product.selected}
                                onCheckedChange={() => toggleProductSelection(product.id)}
                              />
                            </TableCell>
                            <TableCell>{product.name}</TableCell>
                            <TableCell>{product.category}</TableCell>
                            <TableCell>RM {product.price.toFixed(2)}</TableCell>
                            <TableCell>
                              <Input 
                                type="number" 
                                value={product.discountPrice} 
                                step="0.01"
                                min="0"
                                onChange={(e) => handleDiscountPriceChange(product.id, e.target.value)}
                                className="w-20 h-8"
                              />
                            </TableCell>
                            <TableCell>{product.discountPercent.toFixed(1)}%</TableCell>
                            <TableCell>{product.margin.toFixed(1)}%</TableCell>
                            <TableCell>
                              <Input 
                                type="number" 
                                value={product.limitPerCustomer || ""} 
                                placeholder="No limit"
                                min="0"
                                onChange={(e) => handleLimitChange(product.id, e.target.value)}
                                className="w-20 h-8"
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-4 text-muted-foreground">
                            No products found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
              
              {selectedPromotionType === "Buy X Get Y" && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">Buy X Products</h4>
                        <div className="flex items-center space-x-2">
                          <Label htmlFor="buy-qty">Quantity:</Label>
                          <Input 
                            id="buy-qty" 
                            type="number" 
                            value={buyQty} 
                            onChange={(e) => setBuyQty(Number(e.target.value))}
                            className="w-16 h-8" 
                            min="1"
                          />
                        </div>
                      </div>
                      
                      <div className="border rounded-md overflow-hidden mb-2">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Product Name</TableHead>
                              <TableHead>Price</TableHead>
                              <TableHead className="w-10"></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {buyXProducts.length > 0 ? (
                              buyXProducts.map((product) => (
                                <TableRow key={product.id}>
                                  <TableCell>{product.name}</TableCell>
                                  <TableCell>RM {product.price.toFixed(2)}</TableCell>
                                  <TableCell>
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      onClick={() => removeProductFromBuyX(product.id)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={3} className="text-center py-2 text-muted-foreground">
                                  No products selected
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                      
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Plus className="mr-2 h-4 w-4" />
                            Add Product
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80">
                          <div className="space-y-2">
                            <h4 className="font-medium">Select Product</h4>
                            <Input 
                              placeholder="Search products..." 
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              className="mb-2"
                            />
                            <div className="max-h-60 overflow-y-auto">
                              {filteredProducts.map(product => (
                                <div 
                                  key={product.id} 
                                  className="flex items-center justify-between p-2 hover:bg-muted rounded-md cursor-pointer"
                                  onClick={() => addProductToBuyX(product)}
                                >
                                  <span>{product.name}</span>
                                  <span>RM {product.price.toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                    
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">Get Y Products Free</h4>
                        <div className="flex items-center space-x-2">
                          <Label htmlFor="get-qty">Quantity:</Label>
                          <Input 
                            id="get-qty" 
                            type="number" 
                            value={getQty} 
                            onChange={(e) => setGetQty(Number(e.target.value))}
                            className="w-16 h-8" 
                            min="1"
                          />
                        </div>
                      </div>
                      
                      <div className="border rounded-md overflow-hidden mb-2">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Product Name</TableHead>
                              <TableHead>Price</TableHead>
                              <TableHead className="w-10"></TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {getFreeYProducts.length > 0 ? (
                              getFreeYProducts.map((product) => (
                                <TableRow key={product.id}>
                                  <TableCell>{product.name}</TableCell>
                                  <TableCell>RM {product.price.toFixed(2)}</TableCell>
                                  <TableCell>
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      onClick={() => removeProductFromGetY(product.id)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={3} className="text-center py-2 text-muted-foreground">
                                  No products selected
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                      
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="sm">
                            <Plus className="mr-2 h-4 w-4" />
                            Add Product
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80">
                          <div className="space-y-2">
                            <h4 className="font-medium">Select Product</h4>
                            <Input 
                              placeholder="Search products..." 
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              className="mb-2"
                            />
                            <div className="max-h-60 overflow-y-auto">
                              {filteredProducts.map(product => (
                                <div 
                                  key={product.id} 
                                  className="flex items-center justify-between p-2 hover:bg-muted rounded-md cursor-pointer"
                                  onClick={() => addProductToGetY(product)}
                                >
                                  <span>{product.name}</span>
                                  <span>RM {product.price.toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox id="same-product" />
                    <label htmlFor="same-product" className="text-sm font-medium leading-none">
                      Free items must be the same as purchased items
                    </label>
                  </div>
                </div>
              )}
              
              {selectedPromotionType === "Bundle Deal" && (
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="grid gap-2">
                      <Label htmlFor="bundle-price">Bundle Price (RM)</Label>
                      <Input 
                        id="bundle-price" 
                        type="number" 
                        placeholder="50.00" 
                        step="0.01"
                        min="0"
                        value={bundlePrice}
                        onChange={(e) => setBundlePrice(e.target.value)}
                      />
                    </div>
                    <div className="flex items-end">
                      <div className="text-sm text-muted-foreground">
                        Select products to include in this bundle deal
                      </div>
                    </div>
                  </div>
                  
                  <div className="border rounded-md overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">
                            <Checkbox 
                              checked={selectAll}
                              onCheckedChange={toggleSelectAllProducts}
                              id="select-all-bundle"
                            />
                          </TableHead>
                          <TableHead>Product Name</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Normal Price (RM)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredProducts.length > 0 ? (
                          filteredProducts.map((product) => (
                            <TableRow key={product.id}>
                              <TableCell>
                                <Checkbox 
                                  checked={product.selected}
                                  onCheckedChange={() => toggleProductSelection(product.id)}
                                />
                              </TableCell>
                              <TableCell>{product.name}</TableCell>
                              <TableCell>{product.category}</TableCell>
                              <TableCell>RM {product.price.toFixed(2)}</TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-4 text-muted-foreground">
                              No products found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
              
              {selectedPromotionType === "Voucher Code" && (
                <div className="grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="voucher-code">Voucher Code</Label>
                    <Input id="voucher-code" placeholder="SUMMER2025" />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="voucher-discount">Voucher Discount</Label>
                    <div className="flex">
                      <Select defaultValue="percent">
                        <SelectTrigger className="w-[100px] rounded-r-none">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percent">Percent</SelectItem>
                          <SelectItem value="fixed">Fixed</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input id="voucher-discount" type="number" placeholder="10" className="rounded-l-none" />
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Usage Limits */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Usage Limits</h3>
                <Switch
                  id="usage-limits-active"
                  checked={autoApply}
                  onCheckedChange={setAutoApply}
                />
              </div>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="per-customer">Per Customer</Label>
                  <Input id="per-customer" type="number" placeholder="1" />
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="total-redemptions">Total Redemptions</Label>
                  <Input id="total-redemptions" type="number" placeholder="Unlimited" />
                </div>
              </div>
            </div>
            
            {/* Auto Apply */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium">Auto-Apply</h3>
                  <p className="text-sm text-muted-foreground">Automatically apply this promotion without requiring a code</p>
                </div>
                <Switch
                  checked={autoApply}
                  onCheckedChange={setAutoApply}
                />
              </div>
            </div>
            
            {/* Visual Upload */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Visual Upload</h3>
              <p className="text-sm text-muted-foreground">Upload image to display in JimatShop</p>
              
              <div className="grid grid-cols-1 gap-4">
                <div className="flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg h-32 cursor-pointer bg-gray-50 hover:bg-gray-100">
                  <div className="space-y-1 text-center">
                    <div className="flex text-sm text-gray-600">
                      <label htmlFor="promotion-image" className="relative cursor-pointer rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none">
                        <span>Upload an image</span>
                        <Input id="promotion-image" type="file" className="sr-only" />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" type="button">Save as Draft</Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Save promotion without activating</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button type="submit">Create Promotion</Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Create and activate promotion</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreatePromotionTab;
